import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";

/**
 * SuggestInput - text input with history-based autocomplete from a suggestions endpoint
 * Props: value, onChange, endpoint (e.g. "/suggestions/obras"), placeholder, testId
 */
export default function SuggestInput({ value, onChange, endpoint, placeholder, testId, className = "", invalid = false }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const wrapRef = useRef(null);
  const debounce = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(async () => {
      try {
        const { data } = await api.get(endpoint, { params: { q: value || "" } });
        setItems(data || []);
      } catch (_) {
        setItems([]);
      }
    }, 150);
    return () => clearTimeout(debounce.current);
  }, [value, open, endpoint]);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <input
        data-testid={testId}
        data-error={invalid || undefined}
        aria-invalid={invalid || undefined}
        className={`brutal-input w-full ${invalid ? "border-red-500 ring-2 ring-red-200 focus:border-red-500 focus:ring-red-200" : ""}`}
        value={value || ""}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        autoComplete="off"
      />
      {open && items.length > 0 && (
        <ul className="absolute z-30 left-0 right-0 mt-1 bg-white brutal-border brutal-shadow max-h-56 overflow-auto">
          {items.map((it) => (
            <li
              key={it}
              data-testid={`${testId}-suggestion`}
              className="px-3 py-2 text-sm font-medium hover:bg-[#FACC15] cursor-pointer border-b border-zinc-200 last:border-b-0"
              onMouseDown={(e) => { e.preventDefault(); onChange(it); setOpen(false); }}
            >
              {it}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
